import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import LapByLapView from './LapByLapView';
import SpotterPanel from './SpotterPanel';
import CategoryBestSectorsTable from './CategoryBestSectorsTable';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Radio, Loader2, RefreshCw, Wifi, WifiOff, Trophy, Users, Timer, Maximize2, Minimize2, ArrowLeft, Star } from 'lucide-react';
import { API } from '../config/api';

/**
 * DATAPRO "Full Stage" — professional live telemetry panel.
 *
 * Data source: `/api/speedhive/live/events/{leid}/snapshot`, polled every
 * ``POLL_MS`` while the user is on screen. Because the snapshot only
 * carries the driver's LATEST completed-lap sectors (no history), we
 * accumulate personal-best sector times *client-side* by watching the
 * ``laps`` counter for each participant — every time it increments we
 * fold the current ``sector_times_s`` into that driver's local
 * ``bestSectors`` array. The session-wide "record" (purple) is derived
 * on every render as the min across all driver bests.
 *
 * Rendering rules:
 *  - **White**     — regular sector (not a personal best this session).
 *  - **Green**     — the driver's own best sector.
 *  - **Purple**    — the absolute best sector across the whole field.
 *  - **Delta PB**  — last-lap-sector minus driver's best sector.
 *  - **Delta SB**  — last-lap-sector minus overall best sector.
 */

const POLL_MS = 1000;
const PLACEHOLDER_LEID_ID = 'select-live-event';

const fmt = (secs) => {
  if (secs == null || !Number.isFinite(secs) || secs <= 0) return '—';
  const m = Math.floor(secs / 60);
  const s = (secs % 60);
  return m > 0
    ? `${m}:${s.toFixed(3).padStart(6, '0')}`
    : s.toFixed(3);
};

const fmtDelta = (delta) => {
  if (delta == null || !Number.isFinite(delta)) return '—';
  if (Math.abs(delta) < 0.001) return '±0.000';
  const s = delta >= 0 ? '+' : '−';
  return `${s}${Math.abs(delta).toFixed(3)}`;
};

// M:SS.mmm formatter with explicit sign — used for the "Δ Punta viva"
// column. User spec (Feb 28 2026): keep the M:SS.mmm shape even for
// sub-minute deltas so the column stays visually aligned; always
// 3-digit milliseconds; sign is always shown (± for zero).
const fmtDeltaClock = (delta) => {
  if (delta == null || !Number.isFinite(delta)) return '—';
  if (Math.abs(delta) < 0.0005) return '±0:00.000';
  const sign = delta > 0 ? '+' : '−';
  const abs = Math.abs(delta);
  const minutes = Math.floor(abs / 60);
  const remainder = abs - minutes * 60;
  // Force 2-digit seconds and 3-digit ms via toFixed(3).padStart(6, '0').
  const secStr = remainder.toFixed(3).padStart(6, '0');
  return `${sign}${minutes}:${secStr}`;
};

const sectorLabel = (idx) => `S${idx + 1}`;

/**
 * Translate MyLaps' numeric flag code into UI colour + i18n key.
 * Semantics observed empirically (2026-02) — MyLaps does not publish
 * a canonical enum, so we fall back to "unknown" for out-of-range values.
 */
const FLAG_MAP = {
  0: { key: 'flag_none',      cls: 'bg-neutral-700 text-neutral-200' },
  1: { key: 'flag_green',     cls: 'bg-green-600 text-white' },
  2: { key: 'flag_yellow',    cls: 'bg-yellow-500 text-black' },
  3: { key: 'flag_red',       cls: 'bg-red-600 text-white' },
  4: { key: 'flag_chequered', cls: 'bg-neutral-900 text-white border-2 border-dashed border-white' },
};

const blankHistory = (name = null, className = null) => ({
  lastLapSeen: 0,
  bestSectors: [],
  bestLap: null,
  lastLap: null,
  lastSectors: [],
  stickySectors: [],
  bestLapSectors: [],
  pendingRollover: false,
  pendingRolloverPolls: 0,
  name,
  className,
});

// Merge one /history driver record into the client history (keeps fastest).
const mergeSeedEntry = (store, key, d) => {
  const h = store[key] || blankHistory(d.name || null, d.class_name || null);
  const seedSecs = Array.isArray(d.best_sectors) ? d.best_sectors : [];
  const seedLapSecs = Array.isArray(d.best_lap_sectors) && d.best_lap_sectors.length
    ? d.best_lap_sectors : seedSecs;
  const secs = [...(h.bestSectors || [])];
  const meta = [...(h.bestSectorsMeta || [])];
  const seedLaps = Array.isArray(d.best_sectors_laps) ? d.best_sectors_laps : [];
  seedSecs.forEach((v, i) => {
    if (v == null || v <= 0) return;
    if (secs[i] == null || v < secs[i]) secs[i] = v;
    if (secs[i] === v && (!meta[i] || meta[i].time > v)) {
      meta[i] = {
        time: v,
        driver_number: key,
        driver_name: h.name || d.name || `#${key}`,
        lap_number: seedLaps[i] ?? '—',
      };
    }
  });
  h.bestSectors = secs;
  h.bestSectorsMeta = meta;
  if ('best_lap' in d) {
    if (d.best_lap != null && (h.bestLap == null || d.best_lap < h.bestLap)) {
      h.bestLap = d.best_lap;
      h.bestLapSectors = seedLapSecs.map((v) => (v == null ? null : v));
    } else if (!(h.bestLapSectors || []).length) {
      h.bestLapSectors = seedLapSecs.map((v) => (v == null ? null : v));
    }
  }
  h.name = h.name || d.name || null;
  h.className = h.className || d.class_name || null;
  store[key] = h;
};

const LiveTelemetryPanel = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [selectedLeid, setSelectedLeid] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotError, setSnapshotError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [connected, setConnected] = useState(false);
  const [manualRefreshTick, setManualRefreshTick] = useState(0);
  const [compareMode, setCompareMode] = useState('off');   // 'off' | 'pb' | 'sb'
  // Click-to-open: which driver's lap-by-lap detail is currently open in
  // the modal. `null` = closed. Populated with `{raceId, driverNumber}`
  // when a row is clicked and we successfully resolve a race for the
  // current live session.
  const [driverModal, setDriverModal] = useState(null);
  // Cached list of in-progress race_sessions for the currently selected
  // live event (populated once per session-id rotation). Used to resolve
  // a driver row → race_id when the user clicks the pilot to see their
  // lap-by-lap detail. If the AutoLive poller hasn't checkpointed yet
  // for this session, this stays empty and clicks show a friendly toast.
  const [liveRaces, setLiveRaces] = useState([]);

  // Focused drivers — a Set of driver numbers the user has starred to
  // track more closely during a live session. Focused rows get a gold
  // border + subtle glow and are pinned to the top of the table
  // regardless of their theoretical position, so the engineer never
  // has to scroll to find their pilot. Persisted in localStorage
  // keyed by live_event_id so preferences survive reloads and switching
  // between events.
  const [focused, setFocused] = useState(new Set());
  useEffect(() => {
    if (!selectedLeid) { setFocused(new Set()); return; }
    try {
      const raw = localStorage.getItem(`dp_focused_${selectedLeid}`);
      const arr = raw ? JSON.parse(raw) : [];
      setFocused(new Set(Array.isArray(arr) ? arr : []));
    } catch (_) {
      setFocused(new Set());
    }
  }, [selectedLeid]);
  const toggleFocused = (driverNumber) => {
    setFocused((prev) => {
      const next = new Set(prev);
      if (next.has(driverNumber)) next.delete(driverNumber);
      else next.add(driverNumber);
      try {
        localStorage.setItem(
          `dp_focused_${selectedLeid}`,
          JSON.stringify(Array.from(next)),
        );
      } catch (_) { /* localStorage full or disabled — non-fatal */ }
      return next;
    });
  };
  // 'ar' → Argentina only (default); 'global' → all countries.
  // Persisted in localStorage so users don't re-pick on every visit.
  const [scope, setScope] = useState(() => {
    try {
      return localStorage.getItem('dp_live_scope') === 'global' ? 'global' : 'ar';
    } catch {
      return 'ar';
    }
  });
  const historyRef = useRef({});
  // Per-driver Spotter events (ring buffer, most-recent first). Populated
  // in the polling effect every time a lap closes. Read by the Spotter
  // panel. Kept in a ref (not state) to avoid re-rendering the whole
  // panel every poll; `setSpotterTick` bumps a tick to refresh the
  // Spotter subtree only when a NEW lap arrives.
  const spotterEventsRef = useRef({});
  const [spotterTick, setSpotterTick] = useState(0);
  // Monotonically-increasing sector-slot count for this session; declared
  // early because the session-switch effect (below) needs to reset it.
  const sectorCountRef = useRef(0);
  // Speedhive publishes ONE live_event_id per meeting but rotates the
  // `session_id` (`data.id` in /active) as classes/groups rotate. When
  // the session id changes on the wire we MUST wipe the client-side
  // history: driver #4 in the new group is a DIFFERENT car than #4 in
  // the previous group, and mixing their PBs would poison "Mejor vuelta
  // de la tanda" and the SB reference. Complement to the backend fix
  // that scopes _history_cache by (live_event_id, session_id).
  const sessionIdRef = useRef(null);
  const leidRef = useRef(null);
  leidRef.current = selectedLeid;
  // Consecutive-failure counter. We only surface upstream errors after
  // several polls in a row fail, so brief Cloudflare/Speedhive blips
  // (very common at 1 Hz polling) don't spam a scary banner.
  const failStreakRef = useRef(0);

  // 1) Fetch active live events on mount OR when scope changes — MERGE:
  //   (a) Speedhive's global "paid only" feed (only shows big events),
  //       filtered by country in AR mode or unfiltered in GLOBAL mode.
  //   (b) Admin-configured categories with a live_event_id — the local
  //       feed that lets amateur / free events surface without waiting
  //       for MyLaps to include them in the global list.
  useEffect(() => {
    let cancelled = false;
    setLoadingEvents(true);
    (async () => {
      try {
        const eventsUrl = scope === 'global'
          ? `${API}/speedhive/live/events`
          : `${API}/speedhive/live/events?country=AR`;
        const [globalRes, catsRes] = await Promise.allSettled([
          axios.get(eventsUrl, { withCredentials: true }),
          axios.get(`${API}/speedhive/categories`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        const merged = [];
        const seen = new Set();
        if (globalRes.status === 'fulfilled') {
          for (const e of globalRes.value.data || []) {
            if (!e.live_event_id || seen.has(e.live_event_id)) continue;
            seen.add(e.live_event_id);
            merged.push(e);
          }
        }
        if (catsRes.status === 'fulfilled') {
          for (const cat of catsRes.value.data || []) {
            const leid = (cat.live_event_id || '').trim();
            if (!leid || seen.has(leid)) continue;
            seen.add(leid);
            merged.push({
              live_event_id: leid,
              name: cat.name,
              track_name: cat.organizer_name || null,
              _from_admin: true,
            });
          }
        }
        setEvents(merged);
        // Only auto-select a first event when the user has none picked
        // yet — switching scope should NOT jump them away from a session
        // they're currently watching (if that session survives the new
        // filter, we preserve it; otherwise we default to the first).
        if (merged.length > 0) {
          if (!selectedLeid || !merged.some((e) => e.live_event_id === selectedLeid)) {
            setSelectedLeid(merged[0].live_event_id);
          }
        }
      } catch (err) {
        if (!cancelled) setSnapshotError(err.response?.data?.detail || err.message);
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scope]); // selectedLeid intentionally omitted — the setter guards against overwriting a still-valid pick

  // Seed / merge the server-side session record (cache + persisted laps)
  // into historyRef, keeping the fastest values already seen client-side.
  const seedHistory = async (leid, sid) => {
    try {
      const r = await axios.get(
        `${API}/speedhive/live/events/${encodeURIComponent(leid)}/history`,
        { withCredentials: true, params: sid ? { session_id: sid } : {} },
      );
      if (leidRef.current !== leid) return;
      if (sid && sessionIdRef.current && sessionIdRef.current !== sid) return;
      for (const d of r.data?.drivers || []) {
        mergeSeedEntry(historyRef.current, String(d.number), d);
      }
      setSpotterTick((n) => n + 1);
    } catch {
      // Non-fatal: live polls keep accumulating.
    }
  };

  // 2) Poll the snapshot for the selected event.
  useEffect(() => {
    if (!selectedLeid) return undefined;
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const res = await axios.get(
          `${API}/speedhive/live/events/${encodeURIComponent(selectedLeid)}/snapshot`,
          { withCredentials: true },
        );
        if (cancelled) return;
        const data = res.data;
        // Detect group/session rotation within the same live_event_id.
        // When Speedhive rotates classes (e.g. PROAM FINAL → CADETE FINAL
        // under the same event id), the previous group's PBs must NOT
        // survive into the new session — driver numbers are reused across
        // groups. We wipe the client history and re-seed from /history,
        // which the backend now serves scoped to the current session.
        const incomingSid = data?.session_id ?? null;
        if (incomingSid !== null && sessionIdRef.current !== null
            && incomingSid !== sessionIdRef.current) {
          historyRef.current = {};
          sectorCountRef.current = 0;
          spotterEventsRef.current = {};
          setSpotterTick((n) => n + 1);
          // Fire-and-forget: refetch the server-side history for the
          // NEW session. Non-blocking so the current snapshot renders
          // immediately with what's in it.
          seedHistory(selectedLeid, incomingSid);
        } else if (incomingSid !== null && sessionIdRef.current === null) {
          // First snapshot after selecting: re-seed pinned to the exact session.
          seedHistory(selectedLeid, incomingSid);
        }
        const firstOfSession = incomingSid !== sessionIdRef.current;
        sessionIdRef.current = incomingSid;
        setSnapshot(data);
        setSnapshotError(null);
        setConnected(true);
        setLastFetchedAt(Date.now());
        failStreakRef.current = 0;
        // Update history: fold newly-completed laps into per-driver bests
        // and accumulate "sticky" sector partials for the in-progress lap.
        // Speedhive publishes each sector for a brief window as the driver
        // crosses each timing loop — a single mid-lap poll might see only
        // s2 filled, the next only s1. Naive rendering would flash values
        // in and out. Instead we MERGE non-null slots across polls into
        // `h.stickySectors`; on lap advance we roll that into
        // `h.lastSectors` (the "last completed lap" view).
        for (const p of data.participants || []) {
          const key = String(p.number);
          const h = historyRef.current[key] || {
            lastLapSeen: 0,
            bestSectors: [],
            bestLap: null,
            lastLap: null,
            lastSectors: [],
            stickySectors: [],
            bestLapSectors: [],
            pendingRollover: false,
            pendingRolloverPolls: 0,
            name: p.name,
            className: p.class_name,
          };
          h.name = p.name || h.name;
          h.className = p.class_name || h.className;
          if (firstOfSession && (p.best_sector_times_s || []).length) {
            // Speedhive's own session-best sectors per driver: backfills
            // everything that happened before this panel was opened.
            historyRef.current[key] = h;
            mergeSeedEntry(historyRef.current, key, { name: p.name, best_sectors: p.best_sector_times_s });
          }
          const currentSecs = p.sector_times_s || [];
          if (p.laps > h.lastLapSeen && p.last_time_s) {
            // Lap just closed. Update best-sector + best-lap aggregations
            // BUT LEAVE `h.stickySectors` in place — the user's spec is
            // that the last sector should keep showing "live" (bright)
            // until the driver crosses S1 of the NEW lap. So instead of
            // rolling the sticky into `lastSectors` immediately, we
            // remember the transition is pending and complete it later,
            // when a genuinely new S1 arrives.
            const arrLen = Math.max(h.stickySectors.length, currentSecs.length);
            // Snapshot the closing lap's per-sector times + PB references
            // BEFORE we mutate `bestSectors`. Needed for the Spotter
            // event (compare "this lap" vs "personal best BEFORE this
            // lap") without needing an extra ref.
            const closingSectors = [];
            const prevBestSectors = [...(h.bestSectors || [])];
            for (let i = 0; i < arrLen; i += 1) {
              const val = h.stickySectors[i] ?? currentSecs[i] ?? null;
              closingSectors.push(val);
              if (val != null && val > 0 && (h.bestSectors[i] == null || val < h.bestSectors[i])) {
                h.bestSectors[i] = val;
                // Track WHO set the best-per-sector so the Category
                // Best Partials table can name the record holder.
                h.bestSectorsMeta = h.bestSectorsMeta || [];
                h.bestSectorsMeta[i] = {
                  time: val,
                  driver_number: key,
                  driver_name: h.name || `#${key}`,
                  lap_number: p.laps,
                };
              } else if (val != null && val === h.bestSectors[i]
                  && h.bestSectorsMeta?.[i]?.lap_number === '—') {
                h.bestSectorsMeta[i].lap_number = p.laps;
              }
            }
            if (h.bestLap == null || p.last_time_s < h.bestLap) {
              h.bestLap = p.last_time_s;
              // Capture the sticky-completed sectors as the ACTUAL
              // sectors of this PB lap. Both `Δ Punta viva` and
              // `Pos. Teórica` reference the SB pilot's real best-lap
              // sectors — not the per-sector-min aggregate — because
              // the per-sector min doesn't correspond to any single
              // lap the driver actually ran.
              h.bestLapSectors = [...h.stickySectors];
            }
            h.lastLap = p.last_time_s;
            h.pendingRollover = true;
            h.pendingRolloverPolls = 0; // safety-valve counter (see below)
            h.lastLapSeen = p.laps;
            // Emit a Spotter event for THIS driver — recorded even for
            // non-focused drivers so if the user stars them later the
            // history is still there for the last few laps. Cheap to
            // keep: we cap it at 20 events per driver.
            const secDeltas = [];
            for (let i = 0; i < arrLen; i += 1) {
              const cur = closingSectors[i];
              const pb = prevBestSectors[i];
              if (cur == null || !(cur > 0)) {
                secDeltas.push(null);
              } else if (pb == null) {
                // No prior reference — this IS the first PB.
                secDeltas.push({ delta: 0, kind: 'first', current: cur });
              } else {
                secDeltas.push({ delta: cur - pb, kind: 'compare', current: cur, prev: pb });
              }
            }
            if (!spotterEventsRef.current[key]) spotterEventsRef.current[key] = [];
            spotterEventsRef.current[key].unshift({
              t: Date.now(),
              lap_number: p.laps,
              lap_time: p.last_time_s,
              sectors: closingSectors,
              sector_deltas: secDeltas,
              driver_name: h.name || `#${key}`,
            });
            spotterEventsRef.current[key] = spotterEventsRef.current[key].slice(0, 20);
            setSpotterTick((n) => n + 1);
          } else if (p.best_time_s && (h.bestLap == null || p.best_time_s < h.bestLap)) {
            // First contact / catch-up: at least remember the best-lap
            // Speedhive already tracks (their number is authoritative).
            h.bestLap = p.best_time_s;
          }
          // Detect a fresh S1 for the NEW lap: value at slot 0 is present
          // AND differs from what we captured for the just-closed lap.
          // Speedhive keeps re-emitting the old lap's sectors for a few
          // polls after closure; the value-compare filter distinguishes
          // "sensor loop still showing yesterday's ping" from "driver
          // has actually crossed the S1 loop of the new lap".
          const oldS1 = h.stickySectors[0];
          const newS1 = currentSecs[0];
          const newS1Arrived = (
            newS1 != null &&
            newS1 > 0 &&
            (oldS1 == null || Math.abs(newS1 - oldS1) >= 0.001)
          );
          if (h.pendingRollover && newS1Arrived) {
            // Commit the transition NOW: the previous lap's sectors
            // demote to `lastSectors` (rendered dim by SectorCell),
            // and sticky restarts empty ready to absorb the new lap.
            h.lastSectors = [...h.stickySectors];
            h.stickySectors = [];
            h.pendingRollover = false;
            h.pendingRolloverPolls = 0;
          } else if (h.pendingRollover) {
            // Safety valve: if the driver never crosses a fresh S1
            // (identical to the previous within 1 ms, pit-in, DNF,
            // parc-fermé), we can't stay in the "everything bright"
            // limbo forever. After ~15 polls (~15 s at 1 Hz) we treat
            // the sticky as belonging to the just-closed lap and let
            // the row render dim like any IN-PIT driver would.
            h.pendingRolloverPolls = (h.pendingRolloverPolls || 0) + 1;
            if (h.pendingRolloverPolls > 15) {
              h.lastSectors = [...h.stickySectors];
              h.stickySectors = [];
              h.pendingRollover = false;
              h.pendingRolloverPolls = 0;
            }
          }
          // Sticky merge: absorb any non-null sector that isn't a
          // re-emit of the previous lap's identical value.
          for (let i = 0; i < currentSecs.length; i += 1) {
            const val = currentSecs[i];
            if (val == null) continue;
            const lastVal = (h.lastSectors || [])[i];
            if (lastVal != null && Math.abs(val - lastVal) < 0.001) continue;
            while (h.stickySectors.length <= i) h.stickySectors.push(null);
            h.stickySectors[i] = val;
          }
          historyRef.current[key] = h;
        }
      } catch (err) {
        if (!cancelled) {
          failStreakRef.current += 1;
          // Only mark the connection down + surface an error after 3
          // consecutive failures. Cloudflare/Speedhive returns brief
          // 502/520 blips several times an hour at this poll rate;
          // showing them instantly makes the panel feel broken while
          // it's really just the upstream flapping.
          if (failStreakRef.current >= 3) {
            setConnected(false);
            const raw = err.response?.data?.detail || err.message || '';
            // Cloudflare's origin-server HTML page slips into `detail`
            // occasionally — replace it with a friendly sentence.
            const looksLikeCloudflare =
              /cloudflare|origin web server|522|520|502/i.test(raw);
            setSnapshotError(
              looksLikeCloudflare
                ? 'Speedhive momentáneamente inestable — reintentando…'
                : raw,
            );
          }
        }
      }
    };

    fetchOnce();
    const id = window.setInterval(fetchOnce, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [selectedLeid, manualRefreshTick]);

  // Fetch race_sessions currently persisted for the selected live event.
  // Used by the click-to-open driver detail modal to resolve a driver
  // row → race_id. Refreshed every time we switch event OR the live
  // session_id rotates (practice → qualy → race). Silent no-op on error.
  useEffect(() => {
    if (!selectedLeid) { setLiveRaces([]); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/races`, {
          withCredentials: true,
          params: { live_event_id: selectedLeid },
        });
        if (cancelled) return;
        const matched = (r.data || []).filter(
          (rc) => rc.speedhive_live_event_id === selectedLeid
        );
        setLiveRaces(matched);
      } catch (_) {
        if (!cancelled) setLiveRaces([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLeid, snapshot?.session_id]);

  // 3) Reset accumulated history when the user switches event, then
  //    SEED it from the server-side rolling record so the panel can
  //    reflect the full session's PB/SB even when the user connects
  //    mid-way through a tanda. Without this seed, joining a session
  //    already in progress would treat each driver's first-observed
  //    lap as their PB and mis-label the real session best.
  useEffect(() => {
    historyRef.current = {};
    spotterEventsRef.current = {};
    setSpotterTick(0);
    setSnapshot(null);
    sectorCountRef.current = 0; // sector-count is per-session; reset on switch
    sessionIdRef.current = null; // session-id is per-event; reset on switch
    if (!selectedLeid) return undefined;
    seedHistory(selectedLeid, null);
    return undefined;
  }, [selectedLeid]);

  // `sectorCount` is a MONOTONICALLY-INCREASING count of sector slots
  // we've ever seen for this session. If a poll happens to arrive with
  // all sectors emptied (Speedhive briefly clears them at lap
  // boundaries), the columns must NOT vanish — they should stay put
  // with previous-lap transparency until values repopulate. `sectorCountRef`
  // is declared above so the session-switch effect can reset it.
  const sectorCount = useMemo(() => {
    let maxSeen = sectorCountRef.current;
    // 1) Highest sector-array length in the current snapshot.
    for (const p of snapshot?.participants || []) {
      const n = (p.sector_times_s || []).length;
      if (n > maxSeen) maxSeen = n;
    }
    // 2) Highest sector-array length among ALL accumulated per-driver
    //    history (bestSectors seeded from /history, sticky partials
    //    from live polling, lastSectors from just-closed laps).
    for (const h of Object.values(historyRef.current)) {
      const n = Math.max(
        (h.bestSectors || []).length,
        (h.stickySectors || []).length,
        (h.lastSectors || []).length,
      );
      if (n > maxSeen) maxSeen = n;
    }
    sectorCountRef.current = maxSeen;
    return maxSeen;
  }, [snapshot]);

  // Session-wide best per sector (purple).
  const overallBestSectors = useMemo(() => {
    const out = new Array(sectorCount).fill(null);
    for (const h of Object.values(historyRef.current)) {
      for (let i = 0; i < sectorCount; i += 1) {
        const s = h.bestSectors?.[i];
        if (s != null && (out[i] == null || s < out[i])) out[i] = s;
      }
    }
    return out;
  }, [sectorCount, snapshot, spotterTick]);

  // Same as `overallBestSectors` but preserves the driver identity + lap
  // number that SET each record — powers the "Mejores Parciales de
  // Categoría" table. Recomputed on every snapshot (piggy-backs on the
  // same tick as sectorCount / spotterTick).
  const overallBestSectorsMeta = useMemo(() => {
    const out = new Array(sectorCount).fill(null);
    for (const h of Object.values(historyRef.current)) {
      const meta = h.bestSectorsMeta || [];
      for (let i = 0; i < sectorCount; i += 1) {
        const m = meta[i];
        if (m && m.time != null && (out[i] == null || m.time < out[i].time)) {
          out[i] = m;
        }
      }
    }
    return out;
    // spotterTick bumps on every closed lap → covers new records too.
  }, [sectorCount, spotterTick, snapshot]);

  const overallBestLap = useMemo(() => {
    let best = null;
    for (const h of Object.values(historyRef.current)) {
      if (h.bestLap != null && (best == null || h.bestLap < best)) best = h.bestLap;
    }
    return best;
  }, [snapshot]);

  const rows = useMemo(() => {
    if (!snapshot?.participants) return [];
    return snapshot.participants.map((p) => {
      const key = String(p.number);
      const h = historyRef.current[key] || {};
      // Display priority per sector slot:
      //   1) h.stickySectors[i] — freshest value we've seen for the
      //      IN-PROGRESS lap (accumulated across polls).
      //   2) h.lastSectors[i]  — the "last completed lap" view (falls
      //      through for slots not yet crossed this new lap).
      //   3) p.sector_times_s[i] — raw snapshot as a safety net.
      // We ALSO expose `sectorSources[i]`:
      //   'current'  → sticky (this lap in progress) — full opacity.
      //   'previous' → carried over from the just-closed lap — dimmed.
      const sticky = h.stickySectors || [];
      const lastCompleted = h.lastSectors || [];
      const raw = p.sector_times_s || [];
      const arrLen = Math.max(sticky.length, lastCompleted.length, raw.length);
      const lastSectors = [];
      const sectorSources = [];
      for (let i = 0; i < arrLen; i += 1) {
        if (sticky[i] != null) {
          lastSectors[i] = sticky[i];
          sectorSources[i] = 'current';
        } else if (lastCompleted[i] != null) {
          lastSectors[i] = lastCompleted[i];
          sectorSources[i] = 'previous';
        } else if (raw[i] != null) {
          lastSectors[i] = raw[i];
          sectorSources[i] = 'previous';
        } else {
          lastSectors[i] = null;
          sectorSources[i] = null;
        }
      }
      // Progress: number of current-lap sectors crossed so far.
      const currentCrossed = sectorSources.filter((s) => s === 'current').length;
      return {
        number: p.number,
        name: p.name,
        position: p.position,
        className: p.class_name,
        laps: p.laps,
        lastLap: p.last_time_s,
        bestLap: h.bestLap ?? p.best_time_s ?? null,
        totalTime: p.total_time_s ?? null,
        diffToLeader: p.diff_to_leader_s ?? null,
        diffToLeaderDisplay: p.diff_to_leader_display ?? null,
        gapPrev: p.gap_prev_s ?? null,
        gapPrevDisplay: p.gap_prev_display ?? null,
        lastSectors,
        sectorSources,
        currentCrossed,
        bestSectors: h.bestSectors || [],
        bestLapSectors: h.bestLapSectors || [],
        sectorsStale: !!p.sectors_stale,
        // Awaiting-S1 state: lap closed but driver hasn't crossed S1 of
        // the new lap yet. Pos. Teórica MUST show `—` during this limbo
        // (per user spec) because we can't yet compare the new lap's
        // partials with the session-best.
        _awaitingS1: !!h.pendingRollover,
        // For live-gap / theoretical-position computation below.
        _currentSectors: sectorSources.map((src, i) =>
          src === 'current' ? lastSectors[i] : null,
        ),
      };
    });
  }, [snapshot, sectorCount]);

  // Derived metrics that require ALL rows in scope: live gap to the
  // SESSION'S BEST LAP on the in-progress lap + hypothetical position
  // ranking against the same reference lap.
  //
  // Reference (per the user's spec, Feb 2026): both metrics must be
  // computed against the "mejor tiempo de vuelta de la sesión, tomando
  // los parciales de esa vuelta" — i.e. the fastest lap ANY driver has
  // done in the current session, and the split it was run in.
  //
  // We find that reference lap by scanning every driver's `bestLap` +
  // `bestLapSectors`: pick the (driver with the smallest bestLap AND a
  // complete bestLapSectors array) as the SB reference. `bestLapSectors`
  // are the ACTUAL sectors of the specific lap that set the driver's PB
  // — NOT the per-sector minimums across their entire session. The user
  // spec (Feb 2026) is explicit: the reference must be the real sectors
  // of the fastest lap that happened, not an impossible composite ideal.
  // If nobody has complete best-lap sectors yet, fall back to the fastest
  // bestLap alone for the live-gap denominator (theoretical pos then falls
  // through to the per-driver best-lap-sector sum as before).
  const enrichedRows = useMemo(() => {
    if (!rows.length) return rows;

    // Build the SESSION-BEST reference.
    // Sort candidates by their AUTHORITATIVE bestLap (Speedhive's
    // btTm) ascending; then walk the list and take the first driver
    // whose bestLapSectors are complete. This guarantees the reference
    // is always "the sectors of the fastest lap in the session" and
    // NOT "the composite whose per-sector sum happens to be lowest"
    // — the latter is exploited by any stale or foreign-group sector
    // value that slipped into a driver's aggregate.
    let sbSectors = null; // list<Optional<float>> aligned to sectorCount
    let sbLap = null;
    const candidates = rows
      .filter((r) => r.bestLap != null && r.bestLap > 0)
      .sort((a, b) => a.bestLap - b.bestLap);
    for (const r of candidates) {
      const bs = r.bestLapSectors || [];
      const complete =
        bs.length === sectorCount &&
        bs.length > 0 &&
        bs.every((s) => s != null && s > 0);
      if (complete) {
        sbSectors = bs;
        sbLap = r.bestLap;
        break;
      }
    }
    // If no driver has a complete best-lap-sectors set yet, fall back
    // to whoever holds the smallest bestLap (may still give a partial
    // sector reference).
    if (!sbSectors && candidates.length) {
      const fastest = candidates[0];
      sbSectors = fastest.bestLapSectors || [];
      sbLap = fastest.bestLap;
    }
    if (!sbSectors) {
      sbSectors = [];
      sbLap = null;
    }

    // ---- Live projection (Feb 2026, refined per user's spec) ----
    // Δ Punta Viva y Pos. Teórica se computan CONTRA los parciales de
    // la vuelta rápida de la sesión (los sectores exactos del SB lap),
    // no contra los mejores per-sector propios del piloto. Fórmula:
    //
    //   projection[i] = currentSectors[i]        (si crossed en vuelta live)
    //                 = sbSectors[i]             (los parciales del SB lap)
    //
    //   tail          = sbLap - Σ sbSectors[i]   (untimed lap remainder)
    //   projectedLap  = Σ projection[i] + tail
    //   liveGap       = Σ (currentSectors[i] - sbSectors[i]) for i crossed
    //   theoreticalPos = rank ascending by projectedLap
    //
    // Con esta fórmula:
    //   • Un piloto que corre EN EL RITMO EXACTO de la SB en cada sector
    //     tiene projectedLap == sbLap → Δ = 0.
    //   • Un piloto más rápido que la SB en los sectores crossed hasta
    //     ahora tiene projectedLap < sbLap → Δ negativo (ganando tiempo).
    //   • Un piloto que aún no cruzó ningún sector proyecta al ritmo
    //     de la SB (baseline neutral) — no se lo penaliza ni premia.
    //
    // La spec anterior usaba `bestSectors` (mínimo per-sector propio) como
    // fallback para slots no cruzados, lo que "premiaba" al piloto por
    // sectores óptimos que quizás nunca haya combinado en la misma vuelta.
    // El usuario aclaró (Feb 2026): "el Δ Punta Viva tiene que ser tomado
    // en base a los mejores parciales de la vuelta rápida de la sesión
    // vs los que venimos haciendo en la vuelta en curso".
    const sbSectorsSum = sbSectors.reduce((a, b) => a + (b != null && b > 0 ? b : 0), 0);
    const tail = sbLap != null && sbSectorsSum > 0 && sbLap > sbSectorsSum
      ? sbLap - sbSectorsSum
      : 0;
    const enriched = rows.map((row) => {
      const cur = row._currentSectors || [];
      let projectedLap = 0;
      let anyProjected = false;
      let ok = true;
      // Σ (cur[i] - sbSectors[i]) for crossed slots — the live delta
      // the engineer reads to know "am I up or down on SB pace".
      let liveCrossedDelta = 0;
      let liveCrossedCount = 0;
      for (let i = 0; i < sectorCount; i += 1) {
        // Segment 1: driver's current sector for this slot (crossed live).
        if (cur[i] != null && cur[i] > 0) {
          projectedLap += cur[i];
          anyProjected = true;
          if (sbSectors[i] != null && sbSectors[i] > 0) {
            liveCrossedDelta += cur[i] - sbSectors[i];
            liveCrossedCount += 1;
          }
          continue;
        }
        // Segment 2: fallback to the SB lap sectors so the projection
        // assumes SB-pace for the untimed remainder. This makes every
        // driver land at sbLap exactly UNTIL they've crossed a sector
        // and diverged from SB pace.
        if (sbSectors[i] != null && sbSectors[i] > 0) {
          projectedLap += sbSectors[i];
          continue;
        }
        // No SB reference for this slot yet (session just started) —
        // can't project this lap.
        ok = false;
        break;
      }
      // Add the untimed tail so the projection is comparable to the
      // FULL lap time (sbLap). Both operands are in the same domain.
      const projected = ok && anyProjected ? projectedLap + tail : null;
      // Δ Punta Viva: sum of per-sector deltas for slots the driver
      // has crossed this lap. When they haven't crossed any yet, we
      // show `null` → UI renders "—".
      const liveGap = liveCrossedCount > 0 ? liveCrossedDelta : null;
      return {
        ...row,
        projectedLap: projected,
        liveGapToLeader: liveGap,
      };
    });

    // ---- Theoretical position: rank by the "puntero-partials" delta ----
    // User spec (Feb 28 2026): ordering is NOT by full projectedLap
    // any more. Only the sectors the driver has ALREADY completed in
    // the current lap-in-progress count. The reference is the SAME
    // sectors of the session-best-lap holder (the "puntero"). Uncrossed
    // sectors don't contribute so a driver that has only crossed S1 is
    // ranked purely on their S1-vs-leader-S1 gap.
    //
    // Formula (per sector i that the driver has crossed):
    //     gap[i]   = cur[i] - sbSectors[i]
    //     delta    = Σ gap[i] over crossed sectors  ==  liveGapToLeader
    //
    // Drivers who haven't crossed any sector in the current lap yet
    // (delta == null) fall to the bottom (no theoretical position).
    //
    // The SB holder themselves ARE ranked with the same rule: if their
    // current lap is running slower than their SB pace, they can slip
    // — that's what "posición teórica viva" is supposed to reflect.
    const rankable = enriched
      .filter((r) => r.liveGapToLeader != null)
      .sort((a, b) => a.liveGapToLeader - b.liveGapToLeader);
    const posMap = new Map(rankable.map((r, idx) => [r.number, idx + 1]));

    return enriched.map((row) => ({
      ...row,
      // Legacy alias so downstream (TelemetryRow, exports, tests) that
      // still read `theoreticalBest` keep working.
      theoreticalBest: row.projectedLap,
      theoreticalPos: posMap.get(row.number) ?? null,
      _sbLap: sbLap,
    }));
  }, [rows, sectorCount]);

  // Focused drivers pinned to the top so trackside engineers never
  // lose sight of their pilot(s). Order among focused rows still
  // follows theoretical position — same for the rest of the field.
  const sortedRows = useMemo(() => {
    const cmp = (a, b) => (a.position || 999) - (b.position || 999);
    const pinned = [];
    const rest = [];
    for (const r of enrichedRows) {
      if (focused.has(r.number)) pinned.push(r);
      else rest.push(r);
    }
    pinned.sort(cmp);
    rest.sort(cmp);
    return [...pinned, ...rest];
  }, [enrichedRows, focused]);

  const secLocale = i18n.language === 'en' ? 'en-US' : 'es-AR';

  // Wall-clock displayed in the CardTitle. Ticks every second — cheap
  // because only this small span re-renders.
  const [wallClock, setWallClock] = useState(() =>
    new Date().toLocaleTimeString(secLocale, { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  );
  useEffect(() => {
    const tick = () => setWallClock(
      new Date().toLocaleTimeString(secLocale, { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    );
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [secLocale]);

  // Fullscreen mode — hides everything else on the page (GlobalTopbar
  // etc.) so trackside engineers get every vertical pixel for the
  // grid. Uses the browser Fullscreen API on the panel's root so
  // ESC exits automatically and the browser chrome disappears too.
  // We set state OPTIMISTICALLY on toggle so the CSS-only fallback
  // (body attribute → hidden topbar) still works on iOS Safari and
  // in embedded webviews where `requestFullscreen` is unavailable or
  // rejected. The `fullscreenchange` listeners reconcile the state
  // when the browser accepts/exits the true fullscreen mode.
  const [isFullscreen, setIsFullscreen] = useState(false);
  const rootRef = useRef(null);
  const toggleFullscreen = async () => {
    const nowFs = !isFullscreen;
    // Optimistically flip state so the CSS-only fallback (topbar
    // hidden via body attribute) engages immediately on browsers
    // that don't support the Fullscreen API.
    setIsFullscreen(nowFs);
    try {
      if (nowFs) {
        // Fullscreen the WHOLE document, not just the panel root.
        // Radix (Shadcn) Select/Dropdown render their menus through
        // React Portals attached to `document.body`. If we only put
        // `rootRef.current` into fullscreen, the body — and therefore
        // the dropdown menus — ends up OUTSIDE the fullscreen stacking
        // context, so clicking the live-event picker in fullscreen
        // mode did literally nothing. Targeting the root
        // `<html>` element keeps every portal inside the visible
        // fullscreen region.
        const el = document.documentElement;
        const req =
          el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
        if (req) await req.call(el);
      } else {
        const exit =
          document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
        if (exit) await exit.call(document);
      }
    } catch {
      /* API rejected — state is already flipped so the class-based
         fallback below still hides the GlobalTopbar. */
    }
  };
  useEffect(() => {
    const onChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement || null;
      setIsFullscreen(!!fsEl);
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);
  // Reflect state on <body> so global CSS can hide the topbar on
  // browsers where requestFullscreen isn't available (iOS Safari).
  useEffect(() => {
    if (isFullscreen) document.body.setAttribute('data-dp-live-fs', '1');
    else document.body.removeAttribute('data-dp-live-fs');
    return () => document.body.removeAttribute('data-dp-live-fs');
  }, [isFullscreen]);

  return (
    <div
      ref={rootRef}
      className="space-y-4"
      style={
        isFullscreen
          ? { background: 'var(--dp-surface-0)', padding: '16px', minHeight: '100vh', overflow: 'auto' }
          : undefined
      }
      data-testid="live-telemetry-panel"
      data-fullscreen={isFullscreen ? '1' : undefined}
    >
      {/* Back-to-hub button — sesions en vivo son una vista dedicada
          (ruta /live) sin nav lateral, así que sin este botón el user
          quedaba forzado a usar la flecha del navegador. */}
      {!isFullscreen && (
        <Button
          onClick={() => navigate('/app')}
          variant="outline"
          className="border-red-600/50 text-red-400 hover:bg-red-950/40 hover:text-red-300 h-9 px-3"
          data-testid="live-panel-back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al inicio
        </Button>
      )}
      {/* HEADER */}
      <Card
        className="border-red-600"
        style={{
          background: 'linear-gradient(to right, color-mix(in srgb, var(--dp-primary) 22%, var(--dp-surface-0)), var(--dp-surface-0))',
        }}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle
                className="text-xl sm:text-2xl flex items-center gap-2 flex-wrap"
                style={{ color: 'var(--dp-text-primary)' }}
              >
                <Radio className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: 'var(--dp-primary)' }} />
                <span className="whitespace-nowrap sm:whitespace-normal">{t('live_panel.title')}</span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap ${
                    connected ? 'border-red-600/40 bg-red-600/15 text-red-500' : 'border-neutral-700 bg-neutral-900 text-neutral-500'
                  }`}
                  data-testid="live-conn-badge"
                >
                  {connected ? (
                    <>
                      <span className="dp-live-dot inline-block h-2 w-2 rounded-full bg-red-500" />
                      <Wifi className="w-3 h-3" /> {t('nav.live')}
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-3 h-3" /> {t('topbar.no_live')}
                    </>
                  )}
                </span>
                {/* Wall clock — tracks side is always juggling schedule vs
                    lap timers, so having the current hour visible in the
                    same title bar avoids a glance-elsewhere. Updates every
                    second via the poll cycle re-render trigger (lastFetchedAt) */}
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider tabular-metric whitespace-nowrap"
                  style={{
                    backgroundColor: 'var(--dp-surface-1)',
                    color: 'var(--dp-text-secondary)',
                  }}
                  data-testid="live-wall-clock"
                >
                  <Timer className="w-3 h-3" />
                  {t('live_panel.wall_clock_label')}: <strong style={{ color: 'var(--dp-text-primary)' }}>{wallClock}</strong>
                </span>
              </CardTitle>
              <p className="text-sm mt-1" style={{ color: 'var(--dp-text-secondary)' }}>
                {t('live_panel.subtitle', { interval: (POLL_MS / 1000).toFixed(1) })}
                {lastFetchedAt && (
                  <span className="ml-2 text-xs" style={{ color: 'var(--dp-text-muted)' }}>
                    · {t('live_panel.last_update')}: {new Date(lastFetchedAt).toLocaleTimeString(secLocale)}
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap min-w-0 max-w-full w-full sm:w-auto">
              {/* FULLSCREEN TOGGLE — pit-lane use, hides everything else */}
              <button
                type="button"
                onClick={toggleFullscreen}
                data-testid="live-fullscreen-toggle"
                className="inline-flex items-center gap-2 h-12 min-h-[48px] px-3 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors active:scale-95"
                style={{
                  backgroundColor: 'var(--dp-surface-1)',
                  border: '1px solid var(--dp-border)',
                  color: 'var(--dp-text-primary)',
                }}
                title={isFullscreen ? t('live_panel.fullscreen_exit') : t('live_panel.fullscreen_enter')}
                aria-label={isFullscreen ? t('live_panel.fullscreen_exit') : t('live_panel.fullscreen_enter')}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                <span className="hidden sm:inline">
                  {isFullscreen ? t('live_panel.fullscreen_exit') : t('live_panel.fullscreen_enter')}
                </span>
              </button>

              {/* SCOPE TOGGLE — Argentina (default) or Global */}
              <div
                className="inline-flex items-center rounded-lg overflow-hidden"
                style={{
                  backgroundColor: 'var(--dp-surface-1)',
                  border: '1px solid var(--dp-border)',
                }}
                data-testid="live-scope-toggle"
              >
                {[
                  { id: 'ar', label: 'AR', flag: '🇦🇷' },
                  { id: 'global', label: t('live_panel.scope_global'), flag: '🌍' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => {
                      setScope(opt.id);
                      try { localStorage.setItem('dp_live_scope', opt.id); } catch { /* no-op */ }
                    }}
                    data-testid={`live-scope-${opt.id}`}
                    className="h-12 min-h-[48px] px-3 text-[11px] font-bold uppercase tracking-wider transition-colors active:scale-95"
                    style={{
                      backgroundColor: scope === opt.id ? 'var(--dp-primary)' : 'transparent',
                      color: scope === opt.id ? '#ffffff' : 'var(--dp-text-secondary)',
                    }}
                    title={opt.id === 'ar' ? 'Solo eventos en Argentina' : 'Todos los eventos globales de Speedhive'}
                  >
                    <span className="mr-1">{opt.flag}</span>
                    {opt.label}
                  </button>
                ))}
              </div>

              <Select
                value={selectedLeid || PLACEHOLDER_LEID_ID}
                onValueChange={(v) => {
                  if (v !== PLACEHOLDER_LEID_ID) setSelectedLeid(v);
                }}
                disabled={loadingEvents || events.length === 0}
              >
                <SelectTrigger
                  className="h-12 min-h-[48px] w-full min-w-0 max-w-full sm:w-auto sm:min-w-[280px] [&>span]:truncate"
                  style={{
                    backgroundColor: 'var(--dp-surface-1)',
                    borderColor: 'var(--dp-border)',
                    color: 'var(--dp-text-primary)',
                  }}
                  data-testid="live-event-selector"
                >
                  {loadingEvents ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('common.loading')}
                    </span>
                  ) : (
                    <SelectValue placeholder={t('live_panel.select_event')} />
                  )}
                </SelectTrigger>
                <SelectContent
                  style={{
                    backgroundColor: 'var(--dp-surface-1)',
                    borderColor: 'var(--dp-border)',
                    color: 'var(--dp-text-primary)',
                  }}
                >
                  {events.length === 0 && (
                    <SelectItem value={PLACEHOLDER_LEID_ID} disabled>
                      {t('live_panel.no_events')}
                    </SelectItem>
                  )}
                  {events.map((e) => (
                    <SelectItem
                      key={e.live_event_id}
                      value={e.live_event_id}
                      data-testid={`live-event-opt-${e.live_event_id}`}
                    >
                      {/* In global scope, prefix the country code so
                          engineers can spot which region an event
                          belongs to at a glance. */}
                      {scope === 'global' && e.country_code ? `[${e.country_code}] ` : ''}
                      {e.name}
                      {e.track_name ? ` — ${e.track_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={() => setManualRefreshTick((n) => n + 1)}
                className="h-12 min-h-[48px] active:scale-95"
                variant="outline"
                data-testid="live-refresh-btn"
                style={{
                  borderColor: 'var(--dp-border)',
                  color: 'var(--dp-text-secondary)',
                }}
                aria-label={t('common.loading')}
              >
                <RefreshCw className={`w-4 h-4 ${connected ? '' : 'opacity-50'}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {snapshotError && (
        <div
          className="rounded-md border border-amber-900/40 bg-amber-950/20 p-3 text-sm text-amber-300"
          data-testid="live-error"
        >
          {snapshotError}
        </div>
      )}

      {/* LEGEND + STATS + COMPARE TOGGLE */}
      <div className="flex items-center gap-4 flex-wrap text-xs">
        <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--dp-text-secondary)' }}>
          <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: 'var(--dp-surface-2)' }} />
          {t('live_panel.legend_regular')}
        </span>
        <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--dp-text-secondary)' }}>
          <span className="inline-block w-3 h-3 rounded bg-green-500" />
          {t('live_panel.legend_pb')}
        </span>
        <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--dp-text-secondary)' }}>
          <span className="inline-block w-3 h-3 rounded bg-purple-500" />
          {t('live_panel.legend_sb')}
        </span>
        {overallBestLap && (
          <span className="ml-auto inline-flex items-center gap-1 tabular-metric" style={{ color: 'var(--dp-text-secondary)' }}>
            <Trophy className="w-3.5 h-3.5 text-purple-400" />
            {t('live_panel.session_best_lap')}: <strong style={{ color: 'var(--dp-text-primary)' }}>{fmt(overallBestLap)}</strong>
          </span>
        )}
        <span className="inline-flex items-center gap-1" style={{ color: 'var(--dp-text-muted)' }}>
          <Users className="w-3.5 h-3.5" /> {sortedRows.length}
        </span>
      </div>

      {/* SESSION INFO BAR — flag, race time, leader laps, session/event names */}
      {snapshot && (
        <div
          data-testid="live-session-info-bar"
          className="flex flex-wrap items-center gap-3 rounded-lg p-3"
          style={{ backgroundColor: 'var(--dp-surface-0)', border: '1px solid var(--dp-border)' }}
        >
          {/* Flag pill always renders so the trackside engineer knows
              the current session state. When Speedhive omits the `f`
              field (some sessions ship it as null between race
              segments) we surface a neutral "SIN DATO" label instead
              of hiding the badge — the previous behaviour made the
              engineer think the flag was green when in reality we
              just didn't know.
              A finished session is force-shown as CHEQUERED regardless
              of what `f` reads — Speedhive keeps returning `f=3` (red)
              for closed tandas because the session record is stored
              in the red-flag state when it closes. */}
          {(() => {
            const effectiveFlag = snapshot.display_flag !== undefined
              ? snapshot.display_flag
              : (snapshot.session_ended ? 4 : snapshot.flag_status);
            const flagCfg = effectiveFlag != null
              ? (FLAG_MAP[effectiveFlag] || FLAG_MAP[0])
              : null;
            return (
              <span
                data-testid="live-flag-pill"
                data-flag-code={effectiveFlag != null ? String(effectiveFlag) : undefined}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${
                  flagCfg ? flagCfg.cls : 'bg-neutral-500/25 text-neutral-300'
                }`}
                title={
                  snapshot.session_ended
                    ? t('live_panel.flag_chequered')
                    : (effectiveFlag == null ? t('live_panel.flag_unknown_tooltip') : undefined)
                }
              >
                <span className="dp-live-dot inline-block h-2 w-2 rounded-full bg-current opacity-80" />
                {flagCfg
                  ? t(`live_panel.${flagCfg.key}`)
                  : t('live_panel.flag_unknown')}
              </span>
            );
          })()}
          {snapshot.race_time && (
            <span className="inline-flex items-center gap-1 tabular-metric" style={{ color: 'var(--dp-text-secondary)' }}>
              <Timer className="w-3.5 h-3.5" />
              <span className="text-[10px] uppercase tracking-wider">{t('live_panel.info_race_time')}:</span>
              <strong style={{ color: 'var(--dp-text-primary)' }} data-testid="live-race-time">{snapshot.race_time}</strong>
            </span>
          )}
          {snapshot.leader_laps != null && (
            <span className="inline-flex items-center gap-1" style={{ color: 'var(--dp-text-secondary)' }}>
              <span className="text-[10px] uppercase tracking-wider">{t('live_panel.info_leader_laps')}:</span>
              <strong className="tabular-metric" style={{ color: 'var(--dp-text-primary)' }}>{snapshot.leader_laps}</strong>
            </span>
          )}
          {snapshot.event_name && (
            <span className="inline-flex items-center gap-1 truncate" style={{ color: 'var(--dp-text-secondary)' }}>
              <span className="text-[10px] uppercase tracking-wider">{t('live_panel.info_event')}:</span>
              <span className="truncate max-w-[280px]" style={{ color: 'var(--dp-text-primary)' }}>{snapshot.event_name}</span>
            </span>
          )}
          {snapshot.session_name && (
            <span className="inline-flex items-center gap-1 truncate" style={{ color: 'var(--dp-text-secondary)' }}>
              <span className="text-[10px] uppercase tracking-wider">{t('live_panel.info_session')}:</span>
              <span className="truncate max-w-[220px]" style={{ color: 'var(--dp-text-primary)' }}>{snapshot.session_name}</span>
            </span>
          )}
          {snapshot.group_name && (
            <span className="inline-flex items-center gap-1 truncate" style={{ color: 'var(--dp-text-secondary)' }} data-testid="live-info-group">
              <span className="text-[10px] uppercase tracking-wider">{t('live_panel.info_group')}:</span>
              <span className="truncate max-w-[200px]" style={{ color: 'var(--dp-text-primary)' }}>{snapshot.group_name}</span>
            </span>
          )}
          {snapshot.session_type_code != null && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
              style={{
                backgroundColor: 'var(--dp-surface-2)',
                color: 'var(--dp-text-primary)',
                border: '1px solid var(--dp-border)',
              }}
              data-testid="live-info-sess-type"
              title={t('live_panel.session_type_tooltip')}
            >
              {t(`live_panel.session_type_${snapshot.session_type_code}`, {
                defaultValue: `Tipo ${snapshot.session_type_code}`,
              })}
            </span>
          )}
          {snapshot.target_laps > 0 && (
            <span className="inline-flex items-center gap-1 tabular-metric" style={{ color: 'var(--dp-text-secondary)' }} data-testid="live-info-target-laps">
              <span className="text-[10px] uppercase tracking-wider">{t('live_panel.info_target')}:</span>
              <strong style={{ color: 'var(--dp-text-primary)' }}>
                {snapshot.leader_laps ?? 0}/{snapshot.target_laps}
              </strong>
            </span>
          )}
          {snapshot.session_start_tod && (
            <span className="inline-flex items-center gap-1 tabular-metric text-[11px]" style={{ color: 'var(--dp-text-secondary)' }} data-testid="live-info-stod">
              <span className="text-[10px] uppercase tracking-wider">{t('live_panel.info_started_at')}:</span>
              <strong style={{ color: 'var(--dp-text-primary)' }}>
                {/* "22-08-2026 20:05:00.000" → "20:05" — keep it terse */}
                {(snapshot.session_start_tod.split(' ')[1] || snapshot.session_start_tod).slice(0, 5)}
              </strong>
            </span>
          )}

          {/* COMPARE MODE TOGGLE */}
          <div
            className="ml-auto inline-flex items-center gap-1 rounded-lg p-1"
            style={{ backgroundColor: 'var(--dp-surface-1)', border: '1px solid var(--dp-border)' }}
            data-testid="live-compare-toggle"
          >
            <span className="px-2 text-[10px] uppercase tracking-wider" style={{ color: 'var(--dp-text-muted)' }}>
              {t('live_panel.compare_label')}
            </span>
            {['off', 'pb', 'sb'].map((m) => (
              <button
                key={m}
                onClick={() => setCompareMode(m)}
                data-testid={`live-compare-opt-${m}`}
                className="px-2.5 min-h-[36px] rounded text-[11px] font-semibold uppercase transition-colors active:scale-95"
                style={{
                  backgroundColor: compareMode === m ? 'var(--dp-primary)' : 'transparent',
                  color: compareMode === m ? '#ffffff' : 'var(--dp-text-secondary)',
                }}
              >
                {t(`live_panel.compare_${m}`)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* SPOTTER + CATEGORY-RECORDS panels. Both are collapsible and
          shown only when there's an active session/snapshot so the UI
          doesn't clutter the empty state. */}
      {snapshot && sectorCount > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="spotter-records-grid">
          <SpotterPanel
            focused={focused}
            spotterEventsRef={spotterEventsRef}
            spotterTick={spotterTick}
            participantsByNumber={Object.fromEntries((snapshot.participants || []).map((p) => [String(p.number), p]))}
          />
          <CategoryBestSectorsTable
            overallBestSectorsMeta={overallBestSectorsMeta}
            sectorCount={sectorCount}
          />
        </div>
      )}

      {/* TABLE */}
      <Card
        className="border-red-900/30"
        style={{ backgroundColor: 'var(--dp-surface-0)' }}
      >
        <CardContent className="p-0 overflow-x-auto">
          {sortedRows.length === 0 ? (
            <div className="py-16 text-center" style={{ color: 'var(--dp-text-muted)' }}>
              {selectedLeid ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" /> {t('live_panel.waiting_data')}
                </span>
              ) : (
                t('live_panel.pick_event_hint')
              )}
            </div>
          ) : (
            <table
              className="min-w-max w-full text-sm tabular-metric"
              data-testid="live-telemetry-table"
              style={{ color: 'var(--dp-text-primary)' }}
            >
              <thead
                className="dp-live-thead"
                style={{ backgroundColor: 'color-mix(in srgb, var(--dp-surface-1) 70%, transparent)', color: 'var(--dp-text-muted)' }}
              >
                <tr className="uppercase text-[10px] tracking-wider">
                  <th className="px-2 py-2 dp-sticky-col dp-sticky-col-0" style={{ width: '32px' }} title="Seguir piloto"><Star className="w-3.5 h-3.5 text-gray-500 mx-auto" /></th>
                  <th className="px-3 py-2 text-left whitespace-nowrap dp-sticky-col dp-sticky-col-1">#</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap dp-sticky-col dp-sticky-col-2">{t('live_panel.col_number')}</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">{t('live_panel.col_driver')}</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">{t('live_panel.col_class')}</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">{t('live_panel.col_laps')}</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">{t('live_panel.col_best')}</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">{t('live_panel.col_last')}</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">{t('live_panel.col_total')}</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">{t('live_panel.col_gap_leader')}</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">{t('live_panel.col_gap_prev')}</th>
                  {Array.from({ length: sectorCount }).map((_, i) => (
                    <th key={i} className="px-3 py-2 text-right whitespace-nowrap" data-testid={`live-sec-head-${i}`}>{sectorLabel(i)}</th>
                  ))}
                  <th className="px-3 py-2 text-right whitespace-nowrap" title={t('live_panel.col_live_gap_tooltip')}>{t('live_panel.col_live_gap')}</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap" title={t('live_panel.col_theoretical_pos_tooltip')}>{t('live_panel.col_theoretical_pos')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => (
                  <TelemetryRow
                    key={r.number}
                    row={r}
                    sectorCount={sectorCount}
                    overallBestSectors={overallBestSectors}
                    overallBestLap={overallBestLap}
                    compareMode={compareMode}
                    sectorsStale={r.sectorsStale}
                    isFocused={focused.has(r.number)}
                    onToggleFocus={toggleFocused}
                    onDriverClick={(driverNumber) => {
                      // Resolve the current live race document. Multi-tier
                      // lookup because older race_sessions may not have
                      // the `speedhive_live_session_id` field (added in
                      // Feb 2026): try session_id first, fall back to
                      // ANY doc matching the live_event_id.
                      const currentSid = snapshot?.session_id || null;
                      const race =
                        (currentSid && liveRaces.find(
                          (rc) => rc.speedhive_live_session_id === currentSid,
                        )) ||
                        liveRaces.find(
                          (rc) => rc.status && rc.status !== 'finalized',
                        ) ||
                        liveRaces[0];
                      if (!race) {
                        // The event isn't being captured yet. Speedhive's
                        // /active feed doesn't expose per-lap sector
                        // history, so we can only serve it once AutoLive
                        // has been accumulating polls. Guide the Pro user
                        // to add the event as a personal category.
                        toast.info(
                          'Este evento todavía no está siendo capturado. Agregalo en "Mis categorías MyLaps" para que empecemos a guardar los parciales.',
                          { duration: 6500 },
                        );
                        return;
                      }
                      setDriverModal({ raceId: race.id, driverNumber, driverName: r.name });
                    }}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Driver detail modal — opens on row click, shows the driver's
          per-lap breakdown for the currently-live session using the
          AutoLive checkpointed race_session doc. */}
      <Dialog
        open={!!driverModal}
        onOpenChange={(open) => { if (!open) setDriverModal(null); }}
      >
        <DialogContent
          className="max-w-6xl bg-neutral-950 border-red-900/40 max-h-[90vh] overflow-y-auto"
          data-testid="live-driver-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-red-500" />
              Detalle en vivo — #{driverModal?.driverNumber} {driverModal?.driverName || ''}
            </DialogTitle>
          </DialogHeader>
          {driverModal && (
            <LapByLapView
              raceId={driverModal.raceId}
              driverNumber={driverModal.driverNumber}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TelemetryRow = React.memo(function TelemetryRow({ row, sectorCount, overallBestSectors, overallBestLap, compareMode, sectorsStale, onDriverClick, isFocused, onToggleFocus }) {
  const isSessionBestLap = row.lastLap != null && overallBestLap != null &&
    Math.abs(row.lastLap - overallBestLap) < 0.001;
  const isPersonalBestLap = row.lastLap != null && row.bestLap != null &&
    Math.abs(row.lastLap - row.bestLap) < 0.001;
  // The DRIVER who currently holds the session best lap. Independent of
  // what their current "last lap" is: the fastest lap in the tanda might
  // have been set 5 laps ago and their `lastLap` since then is slower.
  // Highlighted purple on the "Mejor" cell so at a glance the engineer
  // sees who's leading the tanda's best time.
  const holdsSessionBest = row.bestLap != null && overallBestLap != null &&
    Math.abs(row.bestLap - overallBestLap) < 0.001;

  // Sector-progress hint: how many sectors of the current lap have been
  // captured so far vs the circuit's total sector count. Only render for
  // drivers who ARE on a live lap (`sectorsStale === false`) and have
  // at least one sector crossed this lap. Helps engineers see at a
  // glance who's mid-lap vs who's just closed a lap.
  const showProgress = !sectorsStale && row.currentCrossed > 0 && sectorCount > 0;

  return (
    <tr
      className={`border-t transition-colors hover:bg-[color-mix(in_srgb,var(--dp-surface-1)_40%,transparent)] cursor-pointer ${
        isFocused ? 'bg-yellow-500/[0.06]' : ''
      }`}
      style={{
        borderColor: isFocused ? 'rgb(234 179 8 / 0.55)' : 'var(--dp-border)',
        borderLeft: isFocused ? '3px solid rgb(234 179 8)' : undefined,
        boxShadow: isFocused ? 'inset 0 0 0 1px rgb(234 179 8 / 0.20)' : undefined,
        minHeight: '48px',
      }}
      data-testid={`live-row-${row.number}`}
      data-focused={isFocused ? '1' : undefined}
      onClick={() => onDriverClick && onDriverClick(row.number)}
      role={onDriverClick ? 'button' : undefined}
      title={onDriverClick ? 'Ver detalle vuelta por vuelta' : undefined}
    >
      <td className="px-2 py-2 dp-sticky-col dp-sticky-col-0" style={{ width: '32px' }}>
        {onToggleFocus && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleFocus(row.number); }}
            className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${
              isFocused
                ? 'text-yellow-400 hover:text-yellow-300'
                : 'text-gray-600 hover:text-gray-300'
            }`}
            data-testid={`live-focus-${row.number}`}
            aria-label={isFocused ? 'Quitar del seguimiento' : 'Seguir a este piloto'}
            title={isFocused ? 'Quitar del seguimiento' : 'Seguir a este piloto'}
          >
            <Star className="w-4 h-4" fill={isFocused ? 'currentColor' : 'none'} />
          </button>
        )}
      </td>
      <td className="px-3 py-2 font-semibold dp-sticky-col dp-sticky-col-1" style={{ color: 'var(--dp-text-muted)' }}>{row.position ?? '–'}</td>
      <td className="px-3 py-2 font-bold dp-sticky-col dp-sticky-col-2">#{row.number}</td>
      <td className="px-3 py-2 whitespace-nowrap max-w-[240px] truncate">
        <span>{row.name}</span>
        {showProgress && (
          <span
            className="ml-2 inline-flex items-center gap-1 rounded-full border border-red-600/40 bg-red-600/15 px-1.5 py-0.5 align-middle text-[9px] font-bold uppercase tracking-wider text-red-400"
            data-testid={`live-progress-${row.number}`}
            title={`En vuelta: ${row.currentCrossed}/${sectorCount} parciales`}
          >
            <span className="dp-live-dot inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
            S{row.currentCrossed}/{sectorCount}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-xs" style={{ color: 'var(--dp-text-secondary)' }}>{row.className || '—'}</td>
      <td className="px-3 py-2 text-right">{row.laps}</td>
      <td className="px-3 py-2 text-right" data-testid={`live-best-${row.number}`}>
        <span
          className={holdsSessionBest ? 'font-bold px-1.5 py-0.5 rounded dp-sb-pulse' : ''}
          style={holdsSessionBest ? {
            backgroundColor: 'color-mix(in srgb, var(--dp-purple, #a855f7) 22%, transparent)',
            color: 'var(--dp-purple, #c084fc)',
            boxShadow: '0 0 0 1px color-mix(in srgb, var(--dp-purple, #a855f7) 40%, transparent) inset',
          } : undefined}
          title={holdsSessionBest ? 'Mejor vuelta de la sesión' : undefined}
          data-session-best={holdsSessionBest ? '1' : undefined}
        >
          {fmt(row.bestLap)}
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        <span className={
          isSessionBestLap ? 'text-purple-400 font-bold'
          : isPersonalBestLap ? 'text-green-500 font-bold'
          : ''
        }>
          {fmt(row.lastLap)}
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular-metric" data-testid={`live-total-${row.number}`}>
        {fmt(row.totalTime)}
      </td>
      <td className="px-3 py-2 text-right tabular-metric" data-testid={`live-gap-leader-${row.number}`}>
        {row.diffToLeaderDisplay
          ? <span style={{ color: 'var(--dp-text-secondary)' }}>{row.diffToLeaderDisplay}</span>
          : <span style={{ color: 'var(--dp-text-muted)' }}>—</span>}
      </td>
      <td className="px-3 py-2 text-right tabular-metric" data-testid={`live-gap-prev-${row.number}`}>
        {row.gapPrevDisplay
          ? <span style={{ color: 'var(--dp-text-secondary)' }}>{row.gapPrevDisplay}</span>
          : <span style={{ color: 'var(--dp-text-muted)' }}>—</span>}
      </td>
      {Array.from({ length: sectorCount }).map((_, i) => {
        const val = row.lastSectors[i];
        const pbRef = row.bestSectors[i];
        const sbRef = overallBestSectors[i];
        // Per-slot staleness. Priority (higher wins):
        //   1) Slot source === 'previous' (sector carried over from the
        //      just-closed lap). Rendered VERY transparent (0.22) so
        //      the transition is unmistakable when Última updates.
        //   2) Row-level `sectorsStale` (backend signal: driver IN PIT
        //      / no live lap) — dimmed at 0.45, less aggressive because
        //      the IN PIT state is stable, not a transitional flash.
        //   3) Fresh sticky current-lap sector — full opacity.
        const slotSource = row.sectorSources?.[i];
        let slotOpacity;
        if (slotSource === 'previous') {
          slotOpacity = 0.22;
        } else if (sectorsStale) {
          slotOpacity = 0.45;
        } else {
          slotOpacity = 1;
        }
        let deltaVsRef = null;
        if (compareMode === 'pb' && val != null && pbRef != null) {
          deltaVsRef = val - pbRef;
        } else if (compareMode === 'sb' && val != null && sbRef != null) {
          deltaVsRef = val - sbRef;
        }
        return (
          <SectorCell
            key={i}
            value={val}
            isPB={val != null && pbRef != null && Math.abs(val - pbRef) < 0.001}
            isSB={val != null && sbRef != null && Math.abs(val - sbRef) < 0.001}
            delta={deltaVsRef}
            opacity={slotOpacity}
            testId={`live-sec-${row.number}-${i}`}
          />
        );
      })}
      <td className="px-3 py-2 text-right whitespace-nowrap tabular-metric" data-testid={`live-live-gap-${row.number}`}>
        {row.liveGapToLeader == null ? (
          <span style={{ color: 'var(--dp-text-muted)' }}>—</span>
        ) : Math.abs(row.liveGapToLeader) < 0.001 ? (
          <span
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-purple-500/25 text-purple-300"
            title="Reference — session best sectors"
          >
            SB
          </span>
        ) : (
          <span
            className="font-bold"
            style={{
              color:
                row.liveGapToLeader < 0
                  ? 'rgb(34, 197, 94)' // ganando terreno
                  : 'rgb(239, 68, 68)',
            }}
          >
            {fmtDeltaClock(row.liveGapToLeader)}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-center whitespace-nowrap tabular-metric" data-testid={`live-theo-pos-${row.number}`}>
        {row._awaitingS1 || row.theoreticalPos == null ? (
          <span
            style={{ color: 'var(--dp-text-muted)' }}
            title={row._awaitingS1 ? 'Esperando S1 de la nueva vuelta' : undefined}
          >—</span>
        ) : (
          <span
            className="inline-flex items-center gap-1"
            title={
              row.theoreticalBest != null
                ? `Ideal: ${fmt(row.theoreticalBest)}`
                : undefined
            }
          >
            <span className="font-bold" style={{ color: 'var(--dp-text-primary)' }}>
              P{row.theoreticalPos}
            </span>
            {row.position != null && row.theoreticalPos !== row.position && (
              <span
                className="text-[10px] font-semibold"
                style={{
                  color:
                    row.theoreticalPos < row.position
                      ? 'rgb(34, 197, 94)'
                      : 'rgb(239, 68, 68)',
                }}
              >
                {row.theoreticalPos < row.position ? '▲' : '▼'}
                {Math.abs(row.theoreticalPos - row.position)}
              </span>
            )}
          </span>
        )}
      </td>
    </tr>
  );
});

const SectorCell = React.memo(function SectorCell({ value, isPB, isSB, delta, opacity, testId }) {
  if (value == null || !(value > 0)) {
    return <td className="px-3 py-2 text-right whitespace-nowrap" data-testid={testId} data-empty="1" style={{ color: 'var(--dp-text-muted)' }}>—</td>;
  }
  const cls = isSB ? 'bg-purple-500/25 text-purple-300 font-bold dp-sb-pulse'
    : isPB ? 'bg-green-500/20 text-green-400 font-bold'
    : '';
  const deltaCls = delta == null ? '' : delta < 0 ? 'text-green-400' : delta > 0 ? 'text-red-400' : 'text-neutral-400';
  const isStale = opacity != null && opacity < 1;
  return (
    <td
      className="px-3 py-2 text-right whitespace-nowrap transition-opacity duration-300"
      data-testid={testId}
      data-stale={isStale ? '1' : undefined}
      style={isStale ? { opacity } : undefined}
      title={
        opacity != null && opacity < 0.3
          ? 'Parcial de la vuelta ANTERIOR (recién cerrada)'
          : isStale
          ? 'Parcial de vuelta previa · piloto sin vuelta activa'
          : undefined
      }
    >
      <div className={`inline-flex flex-col items-end rounded px-1.5 py-0.5 min-w-[64px] tabular-metric ${cls}`}>
        <span>{value.toFixed(3)}</span>
        {delta != null && (
          <span className={`text-[10px] font-semibold ${deltaCls}`} data-testid={`${testId}-delta`}>
            {fmtDelta(delta)}
          </span>
        )}
      </div>
    </td>
  );
});

export default LiveTelemetryPanel;
